import z from 'zod';
/**
 * The project's engine movement evidence (DR-3249) — every "this engine went ON" /
 * "this engine came OFF" an `Engine Install Removal Job Cards` document states.
 *
 * ONE envelope per project carrying a flat array, mirroring `bill-of-sale-events-ready`:
 * each job card records its own shop visit, so there is no single winning document to
 * pick, and every row names its own source card instead.
 *
 * `action` is REQUIRED. It is the whole point of the fact — a movement with no
 * direction says nothing about where the engine went — and the generator only ever
 * emits a fact for a page that explicitly attributed a role, so an absent value would
 * be a producer bug rather than a document that stayed silent.
 *
 * No snapshot hash, and `generatedAt` stays the staleness watermark: the array is a union of
 * point-in-time facts rather than a replacing snapshot, so a replayed older envelope adds nothing
 * a newer one already carries. `generation` (DR-5896) names the finalize generation the envelope
 * was built from, so a consumer can tie it to forecast-source's `finalizationGeneration`.
 *
 * CONTRACT: the same `(engineSerial, action)` pair MAY appear several times under different
 * `reconstructedDocumentId`s, and that is EXPECTED. Duplicate uploaded copies of one job card
 * are not collapsed across cards: a job card carries no reliable date, so two cards stating the
 * same movement are indistinguishable from two genuine shop visits, and collapsing them would
 * delete a real one. Consumers must therefore treat each fact as EVIDENCE that a movement is
 * recorded — never as a count of how many times the engine moved.
 */
export const engineMovementEvidenceReadyWebhookSchema = z.looseObject({
    type: z.literal('engine-movement-evidence-ready'),
    projectId: z.string(),
    generatedAt: z.iso.datetime().optional(),
    generation: z.number().int().positive().max(2_147_483_647).optional(),
    facts: z.array(z.looseObject({
        action: z.enum(['installation', 'removal']),
        /** Canonical engine serial the fact is about. */
        engineSerial: z.string(),
        /** Representative live document of the job card's reconstructed entity. */
        documentId: z.string(),
        /** Stable id of the reconstructed job card. */
        reconstructedDocumentId: z.string(),
        /** Earliest card page carrying the observation — the evidence deep-link. */
        evidencePageId: z.string(),
    })),
});
//# sourceMappingURL=engine-movement-evidence-ready.webhook.js.map